import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Checkbox } from '../components/ui/checkbox';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import html2pdf from 'html2pdf.js';
import { 
  Ambulance,
  CheckCircle2,
  AlertTriangle,
  Syringe,
  Shield,
  Activity,
  Download,
  RotateCcw,
  Sparkles,
  Loader2,
  FileText,
  Zap
} from 'lucide-react';

import { API_URL } from '../config';

export default function SMURPage() {
  const { token } = useAuth();
  const printRef = useRef(null);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  
  const [formData, setFormData] = useState(() => {
    try {
      const saved = localStorage.getItem('smur-intervention');
      return saved ? JSON.parse(saved) : {
        typeIntervention: '',
        materiel: {},
        medicaments: {},
        checklistDepart: {},
        observations: ''
      };
    } catch {
      return {
        typeIntervention: '',
        materiel: {},
        medicaments: {},
        checklistDepart: {},
        observations: ''
      };
    }
  });

  useEffect(() => {
    localStorage.setItem('smur-intervention', JSON.stringify(formData));
  }, [formData]);

  const handleCheck = (section, item) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [item]: !prev[section][item]
      }
    }));
  };

  const handleReset = () => {
    if (window.confirm('Réinitialiser ?')) {
      localStorage.removeItem('smur-intervention');
      window.location.reload();
    }
  };

  const handleExportPDF = async () => {
    try {
      toast.info('Génération du PDF...');
      const opt = {
        margin: 10,
        filename: `SMUR-Intervention-${new Date().toISOString().split('T')[0]}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
      };
      await html2pdf().set(opt).from(printRef.current).save();
      toast.success('PDF exporté');
    } catch (error) {
      toast.error('Erreur export PDF');
    }
  };

  const handleAnalyzeAI = async () => {
    try {
      setIsAnalyzing(true);
      toast.info('Analyse IA SMUR en cours...');

      const response = await fetch(`${API_URL}/api/ai/analyze-smur`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) throw new Error('Erreur analyse');
      
      const data = await response.json();
      setAiAnalysis(data);
      toast.success('Analyse terminée');
    } catch (error) {
      toast.error('Erreur analyse IA');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const checklistDepart = [
    'Vérification véhicule (carburant, état)',
    'Scope fonctionnel + électrodes',
    'Défibrillateur chargé',
    'Bouteilles O₂ pleines',
    'Aspirateur fonctionnel',
    'Matériel intubation complet',
    'VVP + cathéters IO disponibles',
    'Médicaments d\'urgence vérifiés',
    'Matériel de protection (gants, masques)',
    'Téléphone + radio opérationnels'
  ];

  const materielEssentiel = [
    'BAVU adulte + pédiatrique',
    'Bouteilles O₂',
    'Sondes intubation (tailles 6-8.5)',
    'Laryngoscope + lames',
    'Aspirateur + sondes aspiration',
    'Scope + électrodes',
    'Défibrillateur',
    'Matériel VVP (14-20G)',
    'Cathéters IO (adulte + péd)',
    'Attelles + immobilisation'
  ];

  const medicamentsUrgence = [
    'Adrénaline 1mg/1ml',
    'Atropine 1mg/1ml',
    'Amiodarone 150mg',
    'Morphine',
    'Midazolam',
    'Kétamine',
    'Propofol',
    'Célocurine',
    'Acide Tranexamique',
    'Aspirine 250mg'
  ];

  return (
    <div className="p-3 md:p-6 space-y-3 md:space-y-4 max-w-5xl mx-auto">
      <div className="space-y-2 print:hidden">
        <div className="flex flex-col sm:flex-row justify-between gap-2">
          <div className="flex items-center gap-2">
            <Ambulance className="w-6 h-6 md:w-8 md:h-8 text-red-400" />
            <div>
              <h1 className="text-lg md:text-2xl font-bold text-[hsl(var(--text))]">SMUR</h1>
              <p className="text-xs text-[hsl(var(--text-dim))]">Protocoles & Checklist SMUR 2025</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleAnalyzeAI} disabled={isAnalyzing} size="sm" className="bg-purple-400/20 text-purple-300 border-purple-400/30">
              {isAnalyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            </Button>
            <Button onClick={handleReset} size="sm" variant="outline" className="text-amber-300 border-amber-400/30">
              <RotateCcw className="w-4 h-4" />
            </Button>
            <Button onClick={handleExportPDF} size="sm" className="bg-cyan-400/20 text-cyan-300 border-cyan-400/30">
              <Download className="w-4 h-4" />
            </Button>
          </div>
        </div>
        <Alert className="bg-red-400/10 border-red-400/30 p-2">
          <Zap className="h-4 w-4 text-red-400" />
          <AlertDescription className="text-xs text-[hsl(var(--text))]">
            <strong>SMUR :</strong> Structure Mobile d'Urgence et de Réanimation
          </AlertDescription>
        </Alert>
      </div>

      <div ref={printRef}>
        <Tabs defaultValue="checklist">
          <TabsList className="grid w-full grid-cols-3 bg-[hsl(var(--bg-elev))]">
            <TabsTrigger value="checklist" className="text-xs md:text-sm">Checklist</TabsTrigger>
            <TabsTrigger value="materiel" className="text-xs md:text-sm">Matériel</TabsTrigger>
            <TabsTrigger value="intervention" className="text-xs md:text-sm">Intervention</TabsTrigger>
          </TabsList>

          <TabsContent value="checklist" className="space-y-3 mt-3">
            <Card className="border-cyan-400/30 bg-cyan-400/5">
              <CardHeader className="p-3">
                <CardTitle className="text-sm md:text-base text-cyan-300 flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4" />
                  CHECKLIST DÉPART SMUR
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3 space-y-2">
                {checklistDepart.map((item, idx) => (
                  <div
                    key={idx}
                    onClick={() => handleCheck('checklistDepart', idx)}
                    className="flex items-center gap-2 p-2 rounded bg-[hsl(var(--bg-elev))] cursor-pointer hover:bg-[hsl(var(--bg-elev))]/80"
                  >
                    <Checkbox checked={formData.checklistDepart[idx] || false} />
                    <span className="text-xs md:text-sm text-[hsl(var(--text))]">{item}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="materiel" className="space-y-3 mt-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Card className="border-blue-400/30 bg-blue-400/5">
                <CardHeader className="p-3">
                  <CardTitle className="text-sm text-blue-300 flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    MATÉRIEL ESSENTIEL
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-3 space-y-1.5">
                  {materielEssentiel.map((item, idx) => (
                    <div
                      key={idx}
                      onClick={() => handleCheck('materiel', idx)}
                      className="flex items-center gap-2 p-1.5 rounded bg-[hsl(var(--bg-elev))] cursor-pointer"
                    >
                      <Checkbox checked={formData.materiel[idx] || false} />
                      <span className="text-xs text-[hsl(var(--text))]">{item}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>

              <Card className="border-purple-400/30 bg-purple-400/5">
                <CardHeader className="p-3">
                  <CardTitle className="text-sm text-purple-300 flex items-center gap-2">
                    <Syringe className="w-4 h-4" />
                    MÉDICAMENTS URGENCE
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-3 space-y-1.5">
                  {medicamentsUrgence.map((item, idx) => (
                    <div
                      key={idx}
                      onClick={() => handleCheck('medicaments', idx)}
                      className="flex items-center gap-2 p-1.5 rounded bg-[hsl(var(--bg-elev))] cursor-pointer"
                    >
                      <Checkbox checked={formData.medicaments[idx] || false} />
                      <span className="text-xs text-[hsl(var(--text))]">{item}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="intervention" className="space-y-3 mt-3">
            <Card className="border-green-400/30 bg-green-400/5">
              <CardHeader className="p-3">
                <CardTitle className="text-sm md:text-base text-green-300 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  BILAN D'INTERVENTION
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3 space-y-3">
                <div>
                  <label className="text-xs font-medium text-[hsl(var(--text))] mb-1.5 block">Type d'intervention</label>
                  <Input
                    value={formData.typeIntervention}
                    onChange={(e) => setFormData(prev => ({ ...prev, typeIntervention: e.target.value }))}
                    placeholder="ACR, SCA, Trauma, Détresse resp..."
                    className="h-9 text-sm"
                  />
                </div>

                <div>
                  <label className="text-xs font-medium text-[hsl(var(--text))] mb-1.5 block">Observations</label>
                  <Textarea
                    value={formData.observations}
                    onChange={(e) => setFormData(prev => ({ ...prev, observations: e.target.value }))}
                    placeholder="Contexte, bilan patient, gestes réalisés, traitements administrés..."
                    className="min-h-[120px] text-xs resize-none"
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {aiAnalysis && (
          <Card className="border-purple-500/30 bg-purple-500/5 mt-3">
            <CardHeader className="p-3">
              <CardTitle className="text-sm md:text-base text-purple-300 flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                ANALYSE IA - Recommandations SMUR
              </CardTitle>
            </CardHeader>
            <CardContent className="p-3">
              <div className="prose prose-sm max-w-none">
                <div className="whitespace-pre-wrap text-xs md:text-sm text-[hsl(var(--text))]">
                  {aiAnalysis.analysis}
                </div>
              </div>
              <Alert className="mt-3 bg-amber-400/10 border-amber-400/30 p-2">
                <AlertDescription className="text-xs text-amber-300">
                  <strong>Disclaimer :</strong> Aide à la décision pré-hospitalière
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
